import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import {
  ArrowUpRight,
  ArrowLeft,
  Plus,
  Minus,
  Check,
  X as XIcon,
  Crown,
  ShieldCheck,
  DollarSign,
  Users2,
  Search,
  MessageSquare,
  BarChart3,
  TrendingUp,
  Filter,
  Zap,
  Lock,
  Sparkles,
} from 'lucide-react';
import ZeditHeader from '../components/zedit/ZeditHeader';
import ZeditFooter from '../components/zedit/ZeditFooter';

const TrueKolPage = () => {
  useEffect(() => {
    document.title = 'TrueKOL · Direct-access KOL network for Web3';
  }, []);

  return (
    <div className="zedit-theme truekol-theme min-h-screen bg-[#0a0616] text-white">
      <ZeditHeader theme="dark" />
      <main>
        <TrueKolHero />
        <NetworkPreviewSection />
        <HowItWorksSection />
        <InteractiveFilterSection />
        <FeaturesSection />
        <UseCasesSection />
        <ComparisonSection />
        <AccessSection />
        <TrueKolFAQ />
        <TrueKolCTA />
      </main>
      <ZeditFooter />
    </div>
  );
};

/* ───────────────── HERO ───────────────── */
const TrueKolHero = () => (
  <section className="relative pt-28 sm:pt-36 lg:pt-44 pb-20 sm:pb-32 overflow-hidden">
    <div className="absolute top-24 left-1/2 -translate-x-1/2 w-[900px] h-[500px] bg-gradient-to-r from-purple-600/30 via-fuchsia-500/20 to-indigo-500/30 blur-[150px] rounded-full -z-10" />
    <div className="absolute inset-0 [background-image:linear-gradient(to_right,rgba(168,85,247,0.06)_1px,transparent_1px),linear-gradient(to_bottom,rgba(168,85,247,0.06)_1px,transparent_1px)] [background-size:48px_48px] [mask-image:radial-gradient(ellipse_at_center,black,transparent_70%)] -z-10" />

    <div className="container mx-auto px-5 sm:px-8 relative">
      <Link
        to="/"
        data-testid="back-to-zedit"
        className="inline-flex items-center gap-1.5 text-xs text-white/50 hover:text-white mb-8 transition-colors"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        Back to Zedit
      </Link>

      <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-500/15 border border-purple-400/30 backdrop-blur-md text-xs font-medium mb-6 text-purple-200">
            <Lock className="w-3.5 h-3.5" />
            Invite-only · 2,500+ vetted KOLs
          </div>

          <motion.h1
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            data-testid="truekol-hero-headline"
            className="text-4xl sm:text-6xl lg:text-7xl xl:text-[82px] font-black tracking-tight leading-[1.04]"
          >
            Direct access.{' '}
            <span className="bg-gradient-to-r from-purple-300 via-fuchsia-300 to-indigo-300 bg-clip-text text-transparent">
              Real pricing.
            </span>
            <br />
            <span className="text-white/70 font-black">Zero agency markup.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mt-6 text-base sm:text-lg lg:text-xl text-white/70 max-w-xl leading-relaxed"
          >
            TrueKOL is a closed network of 1,000+ vetted Web3 KOLs. Verified engagement. Verified pricing.
            Reach out direct · no middleman, no markup, no &ldquo;sourcing fee.&rdquo;
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-10 flex flex-col sm:flex-row gap-3"
          >
            <a
              href="#access"
              data-testid="truekol-hero-cta-primary"
              className="inline-flex items-center justify-center gap-1.5 px-7 py-4 rounded-2xl bg-gradient-to-r from-purple-500 to-fuchsia-500 text-white font-bold text-sm hover:from-purple-400 hover:to-fuchsia-400 transition-all hover:-translate-y-0.5 shadow-lg shadow-purple-500/30"
            >
              Request access
              <ArrowUpRight className="w-4 h-4" />
            </a>
            <a
              href="#how-it-works"
              data-testid="truekol-hero-cta-secondary"
              className="inline-flex items-center justify-center gap-1.5 px-7 py-4 rounded-2xl bg-white/5 border border-white/15 text-white font-semibold text-sm hover:bg-white/10 transition-colors"
            >
              How it works
            </a>
          </motion.div>

          <div className="mt-12 grid grid-cols-3 gap-6 max-w-lg">
            {[
              { v: '2,500+', l: 'Vetted KOLs' },
              { v: '30-50%', l: 'Saved vs agency' },
              { v: '0%', l: 'Markup' },
            ].map((s) => (
              <div key={s.l}>
                <div className="text-2xl sm:text-3xl font-black bg-gradient-to-r from-purple-300 to-fuchsia-300 bg-clip-text text-transparent">{s.v}</div>
                <div className="text-xs text-white/50 mt-1">{s.l}</div>
              </div>
            ))}
          </div>
        </div>

        {/* 3D stacked KOL cards */}
        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="lg:col-span-5 relative"
        >
          <KOLStack />
        </motion.div>
      </div>
    </div>
  </section>
);

const KOLStack = () => {
  const kols = [
    { name: '@DeFi_Maxi', followers: '147K', price: '$2,400', tier: 'Tier 1', niche: 'DeFi / L2s', cpm: '$16 CPM' },
    { name: '@YieldHunter', followers: '89K', price: '$1,200', tier: 'Tier 2', niche: 'Yield / Farming', cpm: '$13 CPM' },
    { name: '@L2_Builder', followers: '215K', price: '$3,800', tier: 'Tier 1', niche: 'Infra / L2', cpm: '$17 CPM' },
  ];
  return (
    <div className="relative h-[480px] sm:h-[540px]">
      {kols.map((k, i) => (
        <motion.div
          key={k.name}
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 + i * 0.12 }}
          whileHover={{ y: -8, rotate: 0, scale: 1.02 }}
          style={{
            top: `${i * 80}px`,
            rotate: `${(i - 1) * 3}deg`,
            zIndex: kols.length - i,
          }}
          className="absolute left-0 right-0 mx-auto w-full max-w-[380px] p-5 rounded-3xl bg-gradient-to-br from-white/10 to-white/[0.03] border border-white/15 backdrop-blur-xl shadow-2xl"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-400 via-fuchsia-400 to-indigo-400" />
                <span className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-purple-500 border-2 border-[#0a0616] flex items-center justify-center">
                  <Check className="w-2.5 h-2.5 text-white" />
                </span>
              </div>
              <div>
                <div className="font-bold text-sm">{k.name}</div>
                <div className="text-[11px] text-white/50">{k.followers} followers · {k.niche}</div>
              </div>
            </div>
            <span className="text-[9px] font-bold px-2 py-0.5 rounded-md bg-purple-500/20 border border-purple-400/30 text-purple-200 uppercase tracking-wider">
              {k.tier}
            </span>
          </div>

          {/* Mini engagement chart */}
          <div className="flex items-end gap-1 h-10 mb-4">
            {[0.4, 0.7, 0.55, 0.8, 0.45, 0.9, 0.6, 0.75, 0.5, 0.85, 0.7, 0.95].map((h, idx) => (
              <div
                key={idx}
                className="flex-1 rounded-sm bg-gradient-to-t from-purple-500/40 to-fuchsia-400"
                style={{ height: `${h * 100}%` }}
              />
            ))}
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div className="p-2 rounded-lg bg-white/[0.03] border border-white/10">
              <div className="text-[9px] text-white/40 uppercase">True price</div>
              <div className="text-sm font-black text-purple-200">{k.price}</div>
            </div>
            <div className="p-2 rounded-lg bg-white/[0.03] border border-white/10">
              <div className="text-[9px] text-white/40 uppercase">CPM</div>
              <div className="text-sm font-black text-white">{k.cpm}</div>
            </div>
            <div className="p-2 rounded-lg bg-purple-500/15 border border-purple-400/30">
              <div className="text-[9px] text-purple-200/70 uppercase">Contact</div>
              <div className="text-sm font-black text-purple-200">Direct</div>
            </div>
          </div>
        </motion.div>
      ))}

      {/* Floating badges */}
      <div className="absolute -bottom-4 left-2 flex items-center gap-2 px-3 py-2 rounded-xl bg-[#0a0616] border border-purple-400/30 shadow-lg shadow-purple-500/20">
        <span className="w-2 h-2 rounded-full bg-purple-400 animate-pulse" />
        <span className="text-xs font-medium">No agency markup</span>
      </div>
    </div>
  );
};

/* ───────────────── Network Preview (scrolling logos) ───────────────── */
const NetworkPreviewSection = () => {
  const tags = ['DeFi', 'L2s', 'Infra', 'Perps', 'Memes', 'AI Agents', 'Yield', 'Stablecoins', 'Bridges', 'Restaking', 'LSTs', 'Privacy'];
  return (
    <section className="py-14 bg-[#0a0616] border-y border-white/5 overflow-hidden">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 mb-6 text-center">
        <span className="text-xs font-semibold uppercase tracking-widest text-purple-300">
          // Slice the network by niche
        </span>
      </div>
      <motion.div
        className="flex gap-3 whitespace-nowrap"
        animate={{ x: ['0%', '-50%'] }}
        transition={{ duration: 40, repeat: Infinity, ease: 'linear' }}
      >
        {[...tags, ...tags].map((t, i) => (
          <span
            key={i}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-purple-500/10 to-fuchsia-500/10 border border-purple-400/20 text-white/70 text-sm font-medium"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-purple-400" />
            {t}
          </span>
        ))}
      </motion.div>
    </section>
  );
};

/* ───────────────── How it works ───────────────── */
const HowItWorksSection = () => {
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });
  const steps = [
    { n: '01', I: ShieldCheck, t: 'We vet', d: 'Every KOL goes through audience-quality checks, engagement audits, and past-delivery reviews before joining.' },
    { n: '02', I: Search, t: 'You filter', d: 'Slice the network by niche, audience region, tier, and historical CPM. Find who actually fits your launch.' },
    { n: '03', I: MessageSquare, t: 'You contact', d: 'Direct Telegram, X DMs, or email. The KOL quoted us their true rate. You talk to them.' },
    { n: '04', I: TrendingUp, t: 'You track', d: 'Campaign outcomes feed back into profiles. The network gets sharper with every deal.' },
  ];
  return (
    <section id="how-it-works" ref={ref} className="py-20 sm:py-28 relative">
      <div className="container mx-auto px-5 sm:px-8">
        <div className="max-w-2xl mb-14">
          <span className="text-xs font-semibold uppercase tracking-widest text-purple-300">
            // How it works
          </span>
          <h2 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]">
            The agency middleman, removed.
          </h2>
          <p className="mt-5 text-base sm:text-lg text-white/70 leading-relaxed">
            Agencies quote you list prices. TrueKOL surfaces the <em>actual</em> price the KOL accepts from your peers.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {steps.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 18 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: i * 0.08 }}
              className="relative rounded-2xl bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 p-6 hover:border-purple-400/50 transition-colors group"
            >
              <div className="flex items-center justify-between mb-4">
                <span className="text-5xl font-black text-purple-500/20">{s.n}</span>
                <div className="w-10 h-10 rounded-xl bg-purple-500/15 flex items-center justify-center group-hover:bg-purple-500/25 transition-colors">
                  <s.I className="w-5 h-5 text-purple-300" />
                </div>
              </div>
              <h3 className="text-base font-bold mb-1.5">{s.t}</h3>
              <p className="text-sm text-white/60 leading-relaxed">{s.d}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ───────────────── Interactive Filter section ───────────────── */
const InteractiveFilterSection = () => {
  const [activeNiche, setActiveNiche] = useState('DeFi');
  const [activeTier, setActiveTier] = useState('All');
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });

  const allKols = [
    { handle: '@DeFi_Maxi', followers: '147K', price: '$2,400', niche: 'DeFi', tier: 'Tier 1', engagement: '4.8%' },
    { handle: '@YieldHunter', followers: '89K', price: '$1,200', niche: 'DeFi', tier: 'Tier 2', engagement: '6.2%' },
    { handle: '@L2_Builder', followers: '215K', price: '$3,800', niche: 'L2s', tier: 'Tier 1', engagement: '3.4%' },
    { handle: '@CryptoBD', followers: '64K', price: '$890', niche: 'Infra', tier: 'Tier 2', engagement: '5.1%' },
    { handle: '@PerpsKing', followers: '180K', price: '$2,900', niche: 'Perps', tier: 'Tier 1', engagement: '4.1%' },
    { handle: '@AI_agentz', followers: '95K', price: '$1,550', niche: 'AI Agents', tier: 'Tier 2', engagement: '7.4%' },
    { handle: '@meme_thesis', followers: '312K', price: '$4,200', niche: 'Memes', tier: 'Tier 1', engagement: '2.9%' },
    { handle: '@rollup_research', followers: '72K', price: '$1,100', niche: 'L2s', tier: 'Tier 2', engagement: '5.8%' },
  ];

  const niches = ['DeFi', 'L2s', 'Infra', 'Perps', 'Memes', 'AI Agents'];
  const tiers = ['All', 'Tier 1', 'Tier 2'];
  const filtered = allKols.filter(
    (k) => k.niche === activeNiche && (activeTier === 'All' || k.tier === activeTier)
  );

  return (
    <section ref={ref} className="py-20 sm:py-28 bg-gradient-to-b from-[#0a0616] via-[#140a24] to-[#0a0616] relative overflow-hidden">
      <div className="absolute top-1/2 right-0 w-[500px] h-[500px] bg-fuchsia-500/10 blur-[150px] rounded-full -z-10" />
      <div className="container mx-auto px-5 sm:px-8">
        <div className="max-w-2xl mb-12">
          <span className="text-xs font-semibold uppercase tracking-widest text-fuchsia-300">
            // Live preview
          </span>
          <h2 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]">
            Filter the network. See real rates.
          </h2>
          <p className="mt-5 text-base sm:text-lg text-white/70 leading-relaxed">
            This is a taste of the TrueKOL dashboard. Try the filters · real prices, real engagement, no markup.
          </p>
        </div>

        {/* Filter bar */}
        <div className="rounded-3xl bg-white/[0.03] border border-white/10 backdrop-blur-xl p-5 sm:p-6">
          <div className="flex flex-wrap items-center gap-3 mb-5">
            <div className="flex items-center gap-2 text-xs text-white/60 mr-2">
              <Filter className="w-3.5 h-3.5" />
              Niche
            </div>
            {niches.map((n) => (
              <button
                key={n}
                onClick={() => setActiveNiche(n)}
                data-testid={`filter-niche-${n.toLowerCase().replace(/\s+/g, '-')}`}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                  activeNiche === n
                    ? 'bg-gradient-to-r from-purple-500 to-fuchsia-500 text-white shadow-lg shadow-purple-500/30'
                    : 'bg-white/5 border border-white/10 text-white/70 hover:bg-white/10'
                }`}
              >
                {n}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-3 pb-5 border-b border-white/10">
            <div className="text-xs text-white/60 mr-2">Tier</div>
            {tiers.map((t) => (
              <button
                key={t}
                onClick={() => setActiveTier(t)}
                data-testid={`filter-tier-${t.toLowerCase().replace(/\s+/g, '-')}`}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                  activeTier === t
                    ? 'bg-white text-[#0a0616]'
                    : 'bg-white/5 border border-white/10 text-white/70 hover:bg-white/10'
                }`}
              >
                {t}
              </button>
            ))}
          </div>

          {/* Results */}
          <div className="mt-5 min-h-[280px]">
            <AnimatePresence mode="wait">
              <motion.div
                key={`${activeNiche}-${activeTier}`}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2 }}
                className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4"
              >
                {filtered.length === 0 ? (
                  <div className="col-span-full text-center py-10 text-white/40 text-sm">
                    No KOLs match this filter. Try another niche.
                  </div>
                ) : (
                  filtered.map((k, i) => (
                    <motion.div
                      key={k.handle}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.05 }}
                      className="relative p-4 rounded-2xl bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 hover:border-purple-400/50 transition-colors group cursor-pointer"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2.5">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-fuchsia-400" />
                          <div>
                            <div className="text-sm font-bold flex items-center gap-1">
                              {k.handle}
                              <ShieldCheck className="w-3 h-3 text-purple-300" />
                            </div>
                            <div className="text-[11px] text-white/50">{k.followers} · {k.engagement} eng</div>
                          </div>
                        </div>
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-200 uppercase">
                          {k.tier.replace('Tier ', 'T')}
                        </span>
                      </div>
                      <div className="flex items-center justify-between pt-3 border-t border-white/10">
                        <div>
                          <div className="text-[10px] text-white/40 uppercase tracking-wider">True price</div>
                          <div className="text-lg font-black text-purple-200">{k.price}</div>
                        </div>
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                          <ArrowUpRight className="w-4 h-4 text-purple-300" />
                        </div>
                      </div>
                    </motion.div>
                  ))
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="mt-6 pt-5 border-t border-white/10 flex items-center justify-between">
            <div className="text-xs text-white/50">
              Showing <span className="text-white font-semibold">{filtered.length}</span> of 2,500+ KOLs · demo slice
            </div>
            <a
              href="#access"
              className="text-xs font-semibold text-purple-300 hover:text-white transition-colors inline-flex items-center gap-1"
            >
              Unlock full network
              <ArrowUpRight className="w-3 h-3" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ───────────────── Features ───────────────── */
const FeaturesSection = () => {
  const [ref, inView] = useInView({ threshold: 0.08, triggerOnce: true });
  const features = [
    { I: ShieldCheck, t: 'Vetted, not random', d: 'Every KOL is reviewed for engagement quality, audience fit, and past delivery before they enter the network.' },
    { I: DollarSign, t: 'True collaboration cost', d: 'See the actual price KOLs charge · not the inflated rate quoted through agencies. Save 30–50% per campaign.' },
    { I: MessageSquare, t: 'Direct contact', d: 'Skip the middlemen. Reach KOLs directly via the contact channel they prefer · Telegram, X DMs, email.' },
    { I: BarChart3, t: 'Performance history', d: 'Past campaigns, engagement rates, CTR, and on-chain conversions. Pick KOLs that actually moved the needle.' },
    { I: Search, t: 'Filter by niche', d: 'DeFi, L2s, infra, perps, memes, AI agents · slice the network by exactly the audience you need.' },
    { I: Users2, t: 'Bulk campaign tooling', d: 'Run 20-KOL campaigns without 20 negotiations. Brief, contract, and pay through one dashboard.' },
  ];
  return (
    <section id="features" ref={ref} className="py-20 sm:py-28">
      <div className="container mx-auto px-5 sm:px-8">
        <div className="max-w-2xl mb-14">
          <span className="text-xs font-semibold uppercase tracking-widest text-purple-300">
            // Features
          </span>
          <h2 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]">
            Everything you need. Nothing you don&apos;t.
          </h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {features.map((f, i) => (
            <motion.div
              key={f.t}
              initial={{ opacity: 0, y: 18 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: i * 0.06 }}
              className="p-6 rounded-2xl bg-white/[0.03] border border-white/10 hover:border-purple-400/50 transition-colors group"
            >
              <div className="w-11 h-11 rounded-xl bg-purple-500/15 flex items-center justify-center mb-4 group-hover:bg-purple-500/25 transition-colors">
                <f.I className="w-5 h-5 text-purple-300" />
              </div>
              <h3 className="text-base font-bold mb-1.5">{f.t}</h3>
              <p className="text-sm text-white/60 leading-relaxed">{f.d}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ───────────────── Use cases ───────────────── */
const UseCasesSection = () => {
  const [ref, inView] = useInView({ threshold: 0.08, triggerOnce: true });
  const cases = [
    { tag: 'Token launch', t: 'Coordinate 30 KOLs in one week, not three.', d: 'Filter by audience fit, send a single brief, and lock 20-30 confirmed posts before TGE · without paying agency margin on top.', outcome: 'Save 30-50% vs agency pricing' },
    { tag: 'Protocol awareness', t: 'Drip thoughtful coverage, not one-shot sponsorship.', d: 'Build a quarterly KOL calendar. Match KOLs to product milestones (launch, integration, mainnet) so coverage builds momentum.', outcome: 'Consistent narrative across the cycle' },
    { tag: 'Community growth', t: 'Buy real users, not bot followers.', d: "Use TrueKOL's engagement filters to pick KOLs whose audience clicks through, joins Discords, and trades. Skip the vanity metrics.", outcome: 'Quality > quantity, every time' },
  ];
  return (
    <section ref={ref} className="py-20 sm:py-28 bg-gradient-to-b from-[#0a0616] to-[#140a24]">
      <div className="container mx-auto px-5 sm:px-8">
        <div className="max-w-2xl mb-14">
          <span className="text-xs font-semibold uppercase tracking-widest text-fuchsia-300">
            // Use cases
          </span>
          <h2 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]">
            Built for the way Web3 actually launches.
          </h2>
        </div>
        <div className="space-y-4">
          {cases.map((u, i) => (
            <motion.div
              key={u.t}
              initial={{ opacity: 0, x: -20 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ delay: i * 0.08 }}
              className="grid lg:grid-cols-12 gap-4 sm:gap-6 p-6 sm:p-8 rounded-2xl bg-white/[0.03] border border-white/10 hover:border-purple-400/40 transition-colors"
            >
              <div className="lg:col-span-1 flex items-start">
                <span className="text-3xl sm:text-4xl font-black text-purple-300">0{i + 1}</span>
              </div>
              <div className="lg:col-span-4">
                <div className="text-xs uppercase tracking-widest text-white/50 mb-2">{u.tag}</div>
                <h3 className="text-xl sm:text-2xl font-bold leading-tight">{u.t}</h3>
              </div>
              <div className="lg:col-span-7">
                <p className="text-sm sm:text-base text-white/70 leading-relaxed">{u.d}</p>
                <div className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-purple-300">
                  <Check className="w-4 h-4" />
                  {u.outcome}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ───────────────── Comparison ───────────────── */
const ComparisonSection = () => {
  const [ref, inView] = useInView({ threshold: 0.08, triggerOnce: true });
  const us = [
    'Direct KOL contact · no account manager in between',
    'True pricing, sourced from real campaigns',
    '2,500+ vetted KOLs across 12 Web3 niches',
    'Bulk briefing and payment in a single dashboard',
    'Performance history on every KOL profile',
    'Invite-only · high-signal, spam-free network',
  ];
  const them = [
    '20–40% agency markup on every quote',
    'List prices, not real negotiated rates',
    '"Preferred partner" rosters of 20–50 KOLs max',
    '1:1 negotiations with each KOL separately',
    'Screenshots and vanity metrics, no raw data',
    'Open-door · anyone with budget gets in',
  ];
  return (
    <section ref={ref} className="py-20 sm:py-28">
      <div className="container mx-auto px-5 sm:px-8">
        <div className="text-center mb-14 max-w-2xl mx-auto">
          <span className="text-xs font-semibold uppercase tracking-widest text-purple-300">
            // TrueKOL vs KOL agencies
          </span>
          <h2 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]">
            A network, not a reseller.
          </h2>
        </div>
        <div className="grid md:grid-cols-2 gap-4 sm:gap-6 max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="relative rounded-3xl bg-gradient-to-br from-purple-500/20 to-fuchsia-500/10 border-2 border-purple-400/40 p-7"
          >
            <div className="inline-flex items-center gap-1.5 text-purple-300 text-xs font-bold uppercase tracking-widest mb-4">
              <Sparkles className="w-3.5 h-3.5" />
              TrueKOL · network
            </div>
            <ul className="space-y-3">
              {us.map((u) => (
                <li key={u} className="flex items-start gap-3 text-sm">
                  <span className="w-5 h-5 rounded-md bg-purple-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-white" />
                  </span>
                  {u}
                </li>
              ))}
            </ul>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 24 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="relative rounded-3xl bg-white/[0.03] border border-white/10 p-7"
          >
            <div className="inline-flex items-center gap-1.5 text-white/60 text-xs font-bold uppercase tracking-widest mb-4">
              Traditional KOL agencies
            </div>
            <ul className="space-y-3">
              {them.map((t) => (
                <li key={t} className="flex items-start gap-3 text-sm text-white/60">
                  <span className="w-5 h-5 rounded-md bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <XIcon className="w-3 h-3 text-white/40" />
                  </span>
                  {t}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

/* ───────────────── Access section (gated) ───────────────── */
const AccessSection = () => (
  <section id="access" className="py-20 sm:py-28 relative overflow-hidden">
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(168,85,247,0.15),transparent_60%)]" />
    <div className="container mx-auto px-5 sm:px-8 relative">
      <div className="max-w-3xl mx-auto rounded-[32px] bg-gradient-to-br from-white/5 to-white/[0.02] border border-purple-400/30 p-8 sm:p-12 backdrop-blur-xl">
        <div className="flex items-center gap-2 mb-6">
          <Crown className="w-5 h-5 text-purple-300" />
          <span className="text-xs font-bold uppercase tracking-widest text-purple-300">Invite-only access</span>
        </div>
        <h2 className="text-3xl sm:text-5xl font-black tracking-tight leading-tight mb-5">
          Apply for a seat.
        </h2>
        <p className="text-base sm:text-lg text-white/70 leading-relaxed mb-8">
          We onboard protocols and brands case-by-case to keep the network high-signal. Tell us who you are, what you&apos;re
          launching, and we&apos;ll respond within 48 hours.
        </p>
        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          {[
            { t: 'Protocols', d: 'L1s, L2s, infra, DeFi, NFTs' },
            { t: 'Brands', d: 'Exchanges, wallets, on-chain apps' },
            { t: 'Funds', d: 'VCs backing their portfolio launches' },
          ].map((a) => (
            <div key={a.t} className="p-4 rounded-xl bg-white/[0.03] border border-white/10">
              <div className="text-sm font-bold text-purple-200">{a.t}</div>
              <div className="text-xs text-white/50 mt-1">{a.d}</div>
            </div>
          ))}
        </div>
        <a
          href="mailto:hi@zedit.io?subject=TrueKOL%20Access%20Request"
          data-testid="truekol-access-cta"
          className="inline-flex items-center gap-1.5 px-7 py-4 rounded-2xl bg-gradient-to-r from-purple-500 to-fuchsia-500 text-white font-bold text-sm hover:from-purple-400 hover:to-fuchsia-400 transition-all hover:-translate-y-0.5 shadow-lg shadow-purple-500/30"
        >
          Request access
          <ArrowUpRight className="w-4 h-4" />
        </a>
      </div>
    </div>
  </section>
);

/* ───────────────── FAQ ───────────────── */
const TrueKolFAQ = () => {
  const [open, setOpen] = useState(0);
  const [ref, inView] = useInView({ threshold: 0.08, triggerOnce: true });
  const faqs = [
    { q: 'How is TrueKOL different from agencies?', a: 'No agency markup. Direct KOL access. Verified pricing data sourced from real campaigns, not list rates.' },
    { q: 'Is access really invite-only?', a: 'Yes. We onboard protocols and brands case-by-case to keep the network high-signal. Sponsors of LayerSeek receive complimentary access.' },
    { q: 'Do you handle payment and contracts?', a: 'Yes · optional. We can route payments and standardize contracts, or you can deal directly with the KOL. Your call.' },
    { q: 'Can I see how a KOL performed before?', a: 'Yes. Each profile includes past campaign data, engagement rates, CTR, and on-chain outcomes when available.' },
    { q: 'How are KOLs vetted?', a: 'We audit audience quality (bot %, geography, wallet overlap), engagement authenticity, and delivery history on past campaigns before anyone enters the network.' },
  ];
  return (
    <section ref={ref} className="py-20 sm:py-28 bg-[#0a0616]">
      <div className="container mx-auto px-5 sm:px-8 max-w-3xl">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-5xl font-black tracking-tight">FAQ</h2>
        </div>
        <div className="space-y-3">
          {faqs.map((f, i) => (
            <motion.div
              key={f.q}
              initial={{ opacity: 0, y: 12 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: i * 0.05 }}
              className="rounded-2xl bg-white/[0.03] border border-white/10 overflow-hidden"
            >
              <button
                onClick={() => setOpen(open === i ? -1 : i)}
                data-testid={`truekol-faq-q-${i}`}
                className="w-full flex items-center justify-between gap-4 p-5 text-left"
              >
                <span className="text-sm sm:text-base font-semibold">{f.q}</span>
                <div className="w-7 h-7 rounded-full bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0">
                  {open === i ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                </div>
              </button>
              {open === i && (
                <div className="px-5 pb-5 text-sm text-white/70 leading-relaxed">{f.a}</div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ───────────────── CTA ───────────────── */
const TrueKolCTA = () => (
  <section className="py-20 sm:py-28 relative overflow-hidden">
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(168,85,247,0.2),transparent_60%)]" />
    <div className="container mx-auto px-5 sm:px-8 text-center relative">
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-500/15 border border-purple-400/30 text-xs font-medium mb-6 text-purple-200">
        <Zap className="w-3.5 h-3.5" />
        48-hour application review
      </div>
      <h2 className="text-4xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[1.05] max-w-3xl mx-auto">
        Stop paying for{' '}
        <span className="bg-gradient-to-r from-purple-300 via-fuchsia-300 to-indigo-300 bg-clip-text text-transparent">
          someone else&apos;s margin.
        </span>
      </h2>
      <p className="mt-6 text-base sm:text-lg text-white/70 max-w-2xl mx-auto">
        Talk to KOLs directly. Pay true rates. Own the relationship.
      </p>
      <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
        <a
          href="mailto:hi@zedit.io?subject=TrueKOL%20Access%20Request"
          data-testid="truekol-cta-primary"
          className="inline-flex items-center justify-center gap-1.5 px-7 py-4 rounded-2xl bg-gradient-to-r from-purple-500 to-fuchsia-500 text-white font-bold text-sm hover:from-purple-400 hover:to-fuchsia-400 transition-all hover:-translate-y-0.5 shadow-lg shadow-purple-500/30"
        >
          Request access
          <ArrowUpRight className="w-4 h-4" />
        </a>
        <a
          href="mailto:hi@zedit.io"
          data-testid="truekol-cta-secondary"
          className="inline-flex items-center justify-center gap-1.5 px-7 py-4 rounded-2xl bg-white/5 border border-white/15 text-white font-semibold text-sm hover:bg-white/10 transition-colors"
        >
          Talk to us
        </a>
      </div>
    </div>
  </section>
);

export default TrueKolPage;
